<template>
  <div v-if="visible" class="help-dialog" :class="{ 'help-dialog-expanded': isFullscreen }">
    <!-- 标题栏 -->
    <div 
      class="help-dialog-header flex items-center justify-between flex-shrink-0 p-12 border-b-f0 cursor-move"
      @mousedown="startDrag"
      @mousemove="onDrag"
      @mouseup="stopDrag"
      @mouseleave="stopDrag"
    >
      <div class="help-dialog-title text-16 color-label font-medium overfloe-ellipsis">
        {{ headerTitle }}
      </div>
      <div class="help-dialog-actions flex items-center gap-8 flex-shrink-0" @mousedown.stop>
        <div class="action-btn" @click="toggleFullscreen">
          <!-- 展开/收起按钮 SVG -->
          <svg v-if="!isFullscreen" width="1em" height="1em" viewBox="0 0 1024 1024" overflow="hidden" fill="currentColor" aria-hidden="true">
            <path d="M445.024 630.6272l-230.384 230.3744-0.0128 0.016a32 32 0 1 1-45.2544-45.2576l0.016-0.0128 230.384-230.3744a32 32 0 0 1 45.2544 45.2544zM838.4 150.4a32 32 0 0 1 22.6272 54.6272l-230.4 230.4a32 32 0 0 1-45.2544-45.2544l230.4-230.4A32 32 0 0 1 838.4 150.4z"></path><path d="M806.4 374.4V214.4h-160c-17.6736 0-32-14.3264-32-32s14.3264-32 32-32h192c17.6736 0 32 14.3264 32 32v192c0 17.6736-14.3264 32-32 32s-32-14.3264-32-32zM217.6 646.4v160h160c17.6736 0 32 14.3264 32 32s-14.3264 32-32 32H185.6c-17.6736 0-32-14.3264-32-32v-192c0-17.6736 14.3264-32 32-32s32 14.3264 32 32z"></path>
          </svg>
          <svg v-else width="1em" height="1em" viewBox="0 0 1024 1024" overflow="hidden" fill="currentColor" aria-hidden="true">
            <path d="M162.9696 815.7696l230.3872-230.3744 0.0128-0.0128a32 32 0 1 1 45.2544 45.2544l-0.016 0.016L208.224 861.024a32 32 0 0 1-45.2544-45.2576zM608 444.8a32 32 0 0 1-22.6272-54.6272l230.4-230.4A32 32 0 0 1 861.024 205.024l-230.4 230.4A32 32 0 0 1 608 444.8z"></path><path d="M640 220.8v160h160c17.6736 0 32 14.3264 32 32s-14.3264 32-32 32h-192c-17.6736 0-32-14.3264-32-32V220.8c0-17.6736 14.3264-32 32-32s32 14.3264 32 32zM390.3968 800v-160h-160c-17.6736 0-32-14.3296-32-32 0-17.6736 14.3264-32 32-32h192c17.6736 0 32 14.3264 32 32v192c0 17.6704-14.3264 32-32 32s-32-14.3296-32-32z"></path>
          </svg>
        </div>
        <div>
          <i class="el-icon-close action-btn" @click="handleClose"></i>
        </div>
      </div>
    </div>    
    <!-- 内容区域 -->
    <div class="help-dialog-body flex flex-col flex-1 overflow-y-auto" style="height: 100%;">
      <div
        class="help-content p-16"
        :style="{ height: contentLoading ? 'calc(100vh - 40px)' : '100%' }"
        ref="helpContent"
        v-loading="contentLoading"
        element-loading-text="加载中..."
        element-loading-background="rgba(255,255,255,1)"
      >
        <div
          class="text-14 color-label"
          v-if="!contentLoading && helpContent.remarks"
          v-html="helpContent.remarks"></div>
      </div>      
    </div>
    <div class="help-content-no-data h-full flex items-center justify-center" v-if="!contentLoading && !helpContent.remarks">
      <img src="@/assets/no_data.png" alt="暂无帮助文档" :class="isFullscreen ? 'help-content-no-data-full' : 'help-content-no-data-small'">
    </div>
    <!-- 图片预览 -->
    <el-image-viewer
      v-if="previewVisible"
      :on-close="() => (previewVisible = false)"
      :url-list="previewList"
      :initial-index="previewIndex"
    />
  </div>
</template>

<script>
import ElImageViewer from 'element-ui/packages/image/src/image-viewer'
import { helpApi } from '@/api/setting/help'
import { sk } from '@/config'
export default {
  name: 'HelpDialog',
  components: { ElImageViewer },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    menuId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      helpContent: {},
      contentLoading: false,
      isFullscreen: false,
      // 当前菜单名称（用于标题显示）
      currentMenuName: '',
      // 预览相关
      previewVisible: false,
      previewList: [],
      previewIndex: 0,
      // 拖拽相关
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      initialLeft: 0,
      initialTop: 0
    }
  },
  computed: {
    // 标题：优先显示当前菜单名称，其次使用接口返回的路径
    headerTitle() {
      // 优先显示当前菜单名称（立即显示）
      if (this.currentMenuName) {
        return `${this.currentMenuName}-使用帮助指南`
      }
      // 其次显示接口返回的菜单路径
      if (this.helpContent.menuNamePath) {
        return `${this.helpContent.menuNamePath}-使用帮助指南`
      }
      // 默认标题
      return '使用帮助指南'
    },
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    // 监听对话框显示状态
    visible: {
      handler(newVisible) {
        if (newVisible) {
          // 每次打开时重置为收缩状态
          this.isFullscreen = false
          // 重置位置到右下角
          this.resetPosition()
          // 获取当前tab的菜单ID并获取帮助信息
          this.getCurrentMenuIdAndHelp()
        }
      }
    },
    // 监听menuId变化
    menuId: {
      handler(newMenuId) {
        if (this.visible && newMenuId) {
          // 当menuId变化时，重新获取帮助信息
          this.getCurrentMenuIdAndHelp()
        }
      },
      immediate: false
    },
    // 监听帮助内容变化，重新应用图片样式
    helpContent: {
      handler() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.applyImageStyles()
          }, 200)
        })
      },
      deep: true
    }
  },
  created() {
    // 组件创建时不需要自动获取帮助信息，等点击帮助按钮时再获取
    // 监听全局菜单切换事件
    this.$bus.$on('menuChanged', this.handleMenuChanged)
  },
  beforeDestroy() {
    // 组件销毁时移除事件监听
    this.$bus.$off('menuChanged', this.handleMenuChanged)
  },
  methods: {
    // 处理菜单切换事件
    handleMenuChanged(menuInfo) {
      if (this.visible && menuInfo) {
        // 更新当前菜单信息
        this.currentMenuName = menuInfo.menuName || menuInfo.name || ''
        // 如果有菜单ID，获取对应的帮助信息
        if (menuInfo.menuId || menuInfo.id) {
          this.getHelpInfo(menuInfo.menuId || menuInfo.id)
        }
      }
    },
    
    // 处理富文本中的图片样式
    processImageStyles() {
      // 使用多重延迟确保图片完全加载
      this.$nextTick(() => {
        setTimeout(() => {
          this.applyImageStyles()
        }, 100)
      })
    },
    
    // 应用图片样式
    applyImageStyles() {
      if (this.$refs.helpContent) {
        const images = this.$refs.helpContent.querySelectorAll('img')
        // 收集图片列表与绑定点击预览
        const urlList = []
        images.forEach((img, idx) => {
          // 强制应用样式，使用!important确保优先级
          img.style.setProperty('max-width', '100%', 'important')
          img.style.setProperty('height', 'auto', 'important')
          img.style.setProperty('object-fit', 'contain', 'important')
          img.style.setProperty('max-width', '100%', 'important')
          img.style.setProperty('display', 'block', 'important')
          img.style.setProperty('margin', '8px 0', 'important')
          img.style.setProperty('border-radius', '4px', 'important')
          img.style.setProperty('cursor', 'zoom-in', 'important')
          
          // 添加类名以便CSS样式覆盖
          img.classList.add('help-image')

          // 记录URL
          const src = img.getAttribute('src') || ''
          if (src) {
            urlList.push(src)
            // 移除旧的事件监听器，避免重复绑定
            img.removeEventListener('click', this.handleImageClick)
            // 点击打开预览
            img.addEventListener('click', () => {
              this.previewList = urlList
              this.previewIndex = idx
              this.previewVisible = true
            })
          }
        })
      }
    },
    
    // 处理图片点击事件
    handleImageClick(event) {
      // 图片点击处理逻辑
    },
    
    // 获取当前tab的菜单ID并获取帮助信息
    async getCurrentMenuIdAndHelp() {
      try {
        let currentMenuId = null
        let currentMenuName = ''
        
        // 优先使用从父组件传递的menuId
        if (this.menuId) {
          currentMenuId = this.menuId
          // 尝试从localStorage获取菜单名称
          const menus = localStorage.getItem(sk('menus'))
          if (menus) {
            try {
              const menuList = JSON.parse(menus)
              currentMenuName = this.findMenuNameById(menuList, currentMenuId) || ''
            } catch (e) {
              // 解析菜单数据失败，忽略错误
            }
          }
        } else {
          // 如果没有传递menuId，则从路由中获取当前菜单ID
          const currentRoute = this.$route
          
          // 方法1: 从路由meta中获取菜单ID和名称
          if (currentRoute.meta && currentRoute.meta.menuId) {
            currentMenuId = currentRoute.meta.menuId
          }
          if (currentRoute.meta && currentRoute.meta.title) {
            currentMenuName = currentRoute.meta.title
          }
          
          // 方法2: 从localStorage中获取当前激活的菜单
          if (!currentMenuId) {
            const menus = localStorage.getItem(sk('menus'))
            if (menus) {
              try {
                const menuList = JSON.parse(menus)
                // 根据当前路径查找对应的菜单ID
                currentMenuId = this.findMenuIdByPath(menuList, currentRoute.path)
                // 根据当前路径查找菜单名称
                if (!currentMenuName) {
                  currentMenuName = this.findMenuNameByPath(menuList, currentRoute.path) || ''
                }
              } catch (e) {
                // 解析菜单数据失败，忽略错误
              }
            }
          }
        }
        
        // 立即设置菜单名称，确保标题能够立即显示
        this.currentMenuName = currentMenuName
        
        if (currentMenuId) {
          await this.getHelpInfo(currentMenuId)
        } else {
        }
      } catch (error) {
      }
    },
    
    // 根据路径查找菜单ID
    findMenuIdByPath(menus, currentPath) {
      // 递归查找菜单ID
      const findInMenu = (menuList) => {
        for (const menu of menuList) {
          // 检查当前菜单是否匹配
          if (menu.path === currentPath) {
            return menu.id
          }
          // 检查子菜单
          if (menu.children && menu.children.length > 0) {
            const childResult = findInMenu(menu.children)
            if (childResult) {
              return childResult
            }
          }
        }
        return null
      }
      
      return findInMenu(menus)
    },
    // 根据路径查找菜单名称
    findMenuNameByPath(menus, currentPath) {
      const findInMenu = (menuList) => {
        for (const menu of menuList) {
          if (menu.path === currentPath) {
            return menu.name || menu.label || menu.title || menu.menuName || ''
          }
          if (menu.children && menu.children.length > 0) {
            const childResult = findInMenu(menu.children)
            if (childResult) return childResult
          }
        }
        return ''
      }
      return findInMenu(menus)
    },
    
    // 根据菜单ID查找菜单名称
    findMenuNameById(menus, menuId) {
      const findInMenu = (menuList) => {
        for (const menu of menuList) {
          // 支持多种ID匹配方式
          if (menu.id === menuId || menu.menuId === menuId || String(menu.id) === String(menuId)) {
            const menuName = menu.name || menu.label || menu.title || menu.menuName || ''
            return menuName
          }
          if (menu.children && menu.children.length > 0) {
            const childResult = findInMenu(menu.children)
            if (childResult) return childResult
          }
        }
        return ''
      }
      return findInMenu(menus)
    },
    
    // 获取帮助文档详情
    async getHelpInfo(menuId = null) {
      const targetMenuId = menuId || this.menuId
      if (!targetMenuId) {
        this.helpContent = { menuNamePath: '请先选择菜单', remarks: '请先选择菜单' }
        return
      }
      
      // 区域loading
      this.contentLoading = true
      
      try {
        const response = await helpApi.getHelpInfo(targetMenuId)
        if (response.code === 200) {
          this.helpContent = response.data || { menuNamePath: '暂无帮助文档', remarks: '暂无帮助文档' }
          // 处理富文本中的图片样式
          this.processImageStyles()
        } else {
        }
      } catch (error) {
      } finally {
        this.contentLoading = false
      }
    },
    // 关闭对话框
    handleClose() {
      // 重置为收缩状态
      this.isFullscreen = false
      this.$emit('update:visible', false)
      this.$emit('close')
    },

    // 切换全屏状态
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
      
      // 如果展开，将弹窗移到最右侧
      if (this.isFullscreen) {
        this.$nextTick(() => {
          const dialog = this.$el
          dialog.style.left = 'auto'
          dialog.style.top = '0'
          dialog.style.right = '0'
          dialog.style.bottom = '0'
        })
      } else {
        // 如果收起，恢复默认位置（右下角）
        this.resetPosition()
      }
    },

    // 重置弹窗位置到右下角
    resetPosition() {
      this.$nextTick(() => {
        const dialog = this.$el
        if (dialog) {
          dialog.style.left = 'auto'
          dialog.style.top = 'auto'
          dialog.style.right = '20px'
          dialog.style.bottom = '20px'
        }
      })
    },
    // 开始拖拽
    startDrag(event) {
      // 如果是在展开状态，不允许拖拽
      if (this.isFullscreen) {
        return
      }
      
      // 检查元素是否存在
      if (!this.$el) {
        return
      }
      
      this.isDragging = true
      this.dragStartX = event.clientX
      this.dragStartY = event.clientY
      
      // 获取当前弹窗的位置
      const dialog = this.$el
      const rect = dialog.getBoundingClientRect()
      this.initialLeft = rect.left
      this.initialTop = rect.top
      
      // 添加拖拽样式类
      if (dialog.classList) {
        dialog.classList.add('dragging')
      }
      
      // 添加全局事件监听
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
      
      // 防止文本选择
      event.preventDefault()
    },

    // 拖拽中
    onDrag(event) {
      if (!this.isDragging || this.isFullscreen || !this.$el) {
        return
      }
      
      // 使用 requestAnimationFrame 提高流畅性
      requestAnimationFrame(() => {
        const deltaX = event.clientX - this.dragStartX
        const deltaY = event.clientY - this.dragStartY
        
        let newLeft = this.initialLeft + deltaX
        let newTop = this.initialTop + deltaY
        
        // 边界检查
        const dialog = this.$el
        if (!dialog) {
          return
        }
        
        const dialogWidth = dialog.offsetWidth
        const dialogHeight = dialog.offsetHeight
        const windowWidth = window.innerWidth
        const windowHeight = window.innerHeight
        
        // 限制在窗口范围内
        newLeft = Math.max(0, Math.min(newLeft, windowWidth - dialogWidth))
        newTop = Math.max(0, Math.min(newTop, windowHeight - dialogHeight))
        
        // 应用新位置
        dialog.style.left = newLeft + 'px'
        dialog.style.top = newTop + 'px'
        dialog.style.right = 'auto'
        dialog.style.bottom = 'auto'
      })
    },

    // 停止拖拽
    stopDrag() {
      this.isDragging = false
      
      // 移除拖拽样式类
      if (this.$el && this.$el.classList) {
        this.$el.classList.remove('dragging')
      }
      
      // 移除全局事件监听
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
    }
  }
}
</script>

<style scoped>
.help-content-no-data-full{
  width: 280px !important;
  height: 280px !important;
}
.help-content-no-data-small{
  width: 240px !important;
  height: 240px !important;
}
.help-content{
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-all;
  white-space: pre-wrap;
  overflow-x: hidden;
}
/* 富文本列表（有序/无序）增加左侧缩进，避免序号/圆点贴边 */
/* 注意：v-html 渲染的内容没有 scoped 特性，需要使用 :deep 才能生效 */
:deep(.help-content ol),
:deep(.help-content ul) {
  padding-left: 12px !important; /* 控制序号/圆点与内容的整体缩进 */
  margin-left: 8px !important;   /* 列表整体与容器的间距 */
  list-style-position: outside !important; /* 序号在内容外侧，避免覆盖文字 */
}
:deep(.help-content li) {
  margin: 4px 0; /* 行间距更友好 */
}
/* 富文本图片样式 - 使用更高优先级的选择器 */
.help-content img,
.help-content img,
.help-content p img,
.help-content span img,
.help-content div img {
  width: 100% !important;
  height: auto !important;
  object-fit: contain !important;
  max-width: 100% !important;
  display: block !important;
  margin: 8px 0 !important;
  border-radius: 4px !important;
}

/* 针对嵌套在span中的图片 */
.help-content span img {
  width: 100% !important;
  height: auto !important;
  object-fit: contain !important;
  max-width: 100% !important;
  display: block !important;
  margin: 8px 0 !important;
  border-radius: 4px !important;
}
/* 帮助对话框基础样式 */
.help-dialog {
  position: fixed;
  top: calc(100vh - 624px);
  left: calc(100vw - 476px);
  width: 400px;
  height: 600px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 12px 24px -16px rgba(0, 0, 0, 0.06), 0 8px 40px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.04);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  /* 硬件加速优化 */
  will-change: transform;
  transform: translateZ(0);
}

/* 展开状态样式 */
.help-dialog-expanded {
  top: 0;
  right: 0;
  bottom: 0;
  left: auto;
  width: 30% !important;
  height: 100vh;
  border-radius: 0;
  box-shadow: 0 12px 24px -16px #0000000f,0 8px 40px #0000001f,0 0 1px #0000000a !important;
}

.action-btn {
  color: #666;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}
.action-btn:hover {
  color: #409eff;
  background-color: #f5f7fa;
}
.action-btn svg {
  width: 16px;
  height: 16px;
}

/* 拖拽相关样式 */
.help-dialog-header {
  user-select: none;
  /* 拖拽时禁用过渡动画 */
  transition: none;
  /* 默认显示手型光标 */
  cursor: move;
}

.help-dialog-header.dragging {
  cursor: move;
}

.help-dialog-actions {
  pointer-events: auto;
}

/* 全展开状态下的头部样式 */
.help-dialog-expanded .help-dialog-header {
  cursor: pointer !important;
  transition: background-color 0.2s ease;
}

/* 全展开状态下，即使不能拖拽也显示手型光标 */
.help-dialog-expanded .help-dialog-header:hover {
  cursor: pointer !important;
  background-color: #f5f7fa !important;
  border-radius: 4px;
}

/* 拖拽时的弹窗样式优化 */
.help-dialog.dragging {
  transition: none;
  /* 拖拽时禁用阴影动画 */
  box-shadow: 0 12px 24px -16px rgba(0, 0, 0, 0.06), 0 8px 40px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.04);
}

/* Loading 高 z-index 样式 */
:deep(.loading-high-zindex) {
  z-index: 4000 !important;
}

:deep(.loading-high-zindex .el-loading-mask) {
  z-index: 4000 !important;
}

/* 帮助文档图片样式 */
:deep(.help-image) {
  max-width: 100% !important;
  height: auto !important;
  object-fit: contain !important;
  max-width: 100% !important;
  display: block !important;
  margin: 8px 0 !important;
  border-radius: 4px !important;
  cursor: zoom-in !important;
}

/* 确保富文本中的图片样式生效 */
:deep(.help-content img) {
  max-width: 100% !important;
  height: auto !important;
  object-fit: contain !important;
  max-width: 100% !important;
  display: block !important;
  margin: 8px 0 !important;
  border-radius: 4px !important;
  cursor: zoom-in !important;
}

</style>

<style>
/* 使内容区域的 v-loading 遮罩在容器内真正居中 */
.help-content {
  position: relative;
}
.help-content > .el-loading-mask {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.help-content > .el-loading-mask .el-loading-spinner {
  top: auto !important;
  margin-top: 0 !important;
}
</style>

